<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Capture {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit, invalid }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onUpdatePresentingQuestions)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <div v-for="(system, i) in systemReview" :key="i" class="mb-3">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="round" style="height: 20px; width: 20px; border-radius: 50%; background-color: gray;"></div>
                  <p class="card-text ml-2">
                    {{ system.complaint }}
                  </p>
                </div>

                <b-button v-b-toggle="`collapse-${i}`" variant="no-outline" class="m-0 p-0">
                  <feather-icon :icon="system.visible ? 'MinusCircleIcon' : 'PlusCircleIcon'" size="25" color="dodgerblue" />
                </b-button>
              </div>

              <b-collapse 
                :id="`collapse-${i}`" 
                class="card-body" 
                style="border-left: 2px solid gray; margin-left: 8px; margin-top: 10px;"
                @hide="system.visible = false" 
                @show="system.visible = true"
              >
                <b-form-group v-for="(question, qi) in system.questions" :key="qi" class="mb-2">
                  <validation-provider #default="{ errors }" :name="question.question" rules="required">
                    <b-row>
                      <b-col cols="12" md="8">
                        <label class="lead text-dark">
                          {{ question.question }}
                        </label>
                      </b-col>

                      <b-col cols="12" md="4">
                        <b-form-radio-group
                          v-if="question.answer_type === 'checkbox'"
                          v-model="question.answer"
                          :options="[
                            { value: true, name: 'Yes' },
                            { value: false, name: 'No' },
                          ]"
                          class=""
                          text-field="name"
                          value-field="value"
                          disabled-field="notEnabled"
                        />

                        <b-form-select
                          v-if="question.answer_type === 'select'"
                          v-model="question.answer"
                          :options="question.select_options"
                          class=""
                        />

                        <b-form-input
                          v-if="question.answer_type === 'text'"
                          v-model="question.answer"
                          class=""
                        />
                      </b-col>
                    </b-row>

                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Form Actions -->
            <div class="d-flex mt-5 pt-2" style="border-top: 2px solid lightgray">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="invalid || disableForm || systemReview.length < 1"
              >
                {{ editingData ? 'Update' : 'Save' }} & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BCollapse, VBToggle, BFormRadioGroup,
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker,
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CommonComplaintSelect from "@/@core/components/shared/admin/selects/CommonComplaintSelect.vue"

import { get } from 'lodash';
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BModal,
    BAlert,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BCollapse,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormDatepicker,
    BFormRadioGroup,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    CommonComplaintSelect
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    resource: {
      type: String,
      required: true
    },
    isAddActive: {
      type: Boolean,
      required: true,
    },
    editingData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    consultationData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    modalTitle: {
      type: String,
      default: 'Record'
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      systemReview: [],
    }
  },
  computed: {
    isUpdate(){
      return !!this.editingData
    },
    disableForm(){
      return this.editingData ? !this.can(`${this.resource}:CanUpdate`) : !this.can(`${this.resource}:CanAddNew`)
    },
  },
  watch: {
    isAddActive: {
      handler(v){
        if (v){
          this.fetchQuestions();
        }
      }
    }
  },
  created(){
    this.fetchQuestions();
  },
  methods: {
    async fetchQuestions() {
      try {
        this.loading = true; 

        const query = {
          consultation_id: this.consultationData._id,
        }

        const request = await this.useJwt().mainService.presentingQuestions.fetchQuestions(query);
        const { data } = request.data;
        this.systemReview = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onUpdatePresentingQuestions(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.isUpdate && !this.can(`${this.resource}:CanAddNew`)) {
          return
        }

        if (this.isUpdate && !this.can(`${this.resource}:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.presentingQuestions.create({
          consultation_id: this.consultationData._id,
          system_review: this.systemReview.map(system => ({
            presenting_complaint_id: system._id,
            questions: system.questions.map(({ _id, answer }) => ({
              answer,
              question_id: _id
            }))
          }))
        });

        this.$emit('refetchData');
        this.$emit('update:is-add-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Presenting questions updated successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
      this.$emit('update:editing-data', null);
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
